import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { paletteColors } from '@evaneos/b2b-ui';

import { Loader } from '../../design-system/Loader';
import { File } from '../../design-system/SVG/File';
import { Yoga } from '../../design-system/SVG/Yoga';
import { GlobalLoader } from '../../shared/components/layout/GlobalLoader';
import { countActiveFilters } from '../../resources/filters/operations';
import { getInTripReviewsRequest } from '../../resources/in-trip-review';
import { getPreTripReviewsRequest } from '../../resources/pre-trip-review';
import { Period } from '../../resources/todo/types';
import { useTrackPageViewAmplitude, ViewedPageTodoProperties } from '../../shared/analytics';
import { useAppDispatch } from '../../shared/redux';
import { parseQueryString } from '../../shared/utilities/queryString';
import { ErrorOverlay } from '../Pipeline/components/Error';
import { useDossierOverviewsFromUrlProcess } from '../../resources/pipeline/hooks';
import { useGroupDossiersByPeriodAndPriorities } from '../../resources/todo/hooks';
import { useGetAlertInTripReviewsInformation } from '../../resources/in-trip-review/hooks';
import { useGetAlertPreTripReviewsInformation } from '../../resources/pre-trip-review/hooks';
import { getDossierOverviewsQueryKey } from '../../resources/dossier-overview/hooks';
import { useGetAgents } from '../../resources/agent/hooks';

import { NextDaysTodo } from './components/NextDaysTodo';
import { TodayTodo } from './components/TodayTodo';
import { TodoLayout } from './components/TodoLayout';
import { TomorrowTodo } from './components/TomorrowTodo';
import s from './Todo.module.css';

type Props = {
    currentDate?: number;
};

export function Todo({
    currentDate = new Date().setHours(0, 0, 0, 0),
}: Readonly<Props>): JSX.Element {
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { period } = useParams() as { period: Period };

    const agentsQuery = useGetAgents();
    const dossiersQuery = useDossierOverviewsFromUrlProcess();

    const isLoading = agentsQuery.isPending || dossiersQuery.isPending;
    const isError = agentsQuery.isError || dossiersQuery.isError;

    const dossiers = useGroupDossiersByPeriodAndPriorities(
        period,
        parseQueryString(location.search),
        currentDate,
    );

    const inTripReviews = useGetAlertInTripReviewsInformation(parseQueryString(location.search));
    const preTripReviews = useGetAlertPreTripReviewsInformation(parseQueryString(location.search));

    const hasPreTripReviews = preTripReviews.length > 0;
    const hasInTripReviews = inTripReviews.length > 0;

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        const viewedPageProperties: ViewedPageTodoProperties = {
            ...defaultProperties,
            todo_tab: period,
        };
        if (period === 'today') {
            viewedPageProperties.has_prebooking_feedback = hasPreTripReviews;
            viewedPageProperties.has_intrip_feedback = hasInTripReviews;
        }
        ampli.viewedPageTodo(viewedPageProperties);
    });

    useEffect(() => {
        dispatch(getPreTripReviewsRequest());
        dispatch(getInTripReviewsRequest());
    }, [dispatch]);

    const query = parseQueryString(location.search);
    const isFiltered = countActiveFilters(query);
    const noDossiersTodo = !dossiers.high.length && !dossiers.medium.length && !dossiers.low.length;
    if (isError) {
        return (
            <TodoLayout>
                <ErrorOverlay
                    onRefresh={() => {
                        void queryClient.invalidateQueries({
                            queryKey: getDossierOverviewsQueryKey(),
                        });
                    }}
                />
            </TodoLayout>
        );
    }
    if (isLoading) {
        return (
            <TodoLayout>
                <div className={s.noDossiersTodo} data-testid="todo-loader">
                    <Loader />
                    <p className={s.loading}>
                        <GlobalLoader />
                    </p>
                </div>
            </TodoLayout>
        );
    }
    if (noDossiersTodo) {
        return (
            <TodoLayout>
                <div
                    className={s.noDossiersTodo}
                    data-testid={isFiltered ? 'empty-with-filter' : 'empty'}
                >
                    {isFiltered ? (
                        <File lineColor={paletteColors.neutral[0]} />
                    ) : (
                        <Yoga lineColor={paletteColors.neutral[0]} strokeWidth="1" />
                    )}
                    <h2>
                        <FormattedMessage
                            id={isFiltered ? 'todo.empty_with_filters' : 'todo.empty'}
                        />
                    </h2>
                </div>
            </TodoLayout>
        );
    }
    return (
        <TodoLayout>
            <div className={s.prioritiesContainer}>
                {period === 'today' && (
                    <TodayTodo
                        hasTripReviews={hasPreTripReviews || hasInTripReviews}
                        preTripReviews={preTripReviews}
                        inTripReviews={inTripReviews}
                        dossiers={dossiers}
                        agents={agentsQuery.data}
                        currentDate={currentDate}
                    />
                )}
                {period === 'tomorrow' && (
                    <TomorrowTodo
                        dossiers={dossiers}
                        agents={agentsQuery.data}
                        currentDate={currentDate}
                    />
                )}
                {period === 'this_week' && (
                    <NextDaysTodo dossiers={dossiers} agents={agentsQuery.data} />
                )}
            </div>
        </TodoLayout>
    );
}
